import {
  BASE_PALETTES,
  type BaseThemePalette,
  type ThemeName,
} from '@bn-games/theming';

export type { ThemeName };

/**
 * Domino theme: extends the cross-game base palette with tile-specific
 * tokens. The "ivory" colors paint the tile face; the "pip" colors paint
 * the pip dots. Tile backs use the board wood palette (already in the
 * base) so the face-down opponent tiles match the table's wood tone.
 */
export interface Theme extends BaseThemePalette {
  readonly monogram: string;

  readonly tileFaceBase: string;
  readonly tileFaceHighlight: string;
  readonly tileFaceShadow: string;
  readonly tileFaceBorder: string;

  readonly tileDivider: string;

  readonly pipColor: string;
  readonly pipShadow: string;

  readonly tileBackBase: string;
  readonly tileBackPattern: string;
  readonly tileBackBorder: string;

  readonly selectedRing: string;
  readonly validArmGlow: string;
}

export const MAHOGANY_THEME: Theme = {
  ...BASE_PALETTES.mahogany,
  monogram: 'D',

  tileFaceBase: '#f4e8ce',
  tileFaceHighlight: '#fff8e2',
  tileFaceShadow: '#c9b58a',
  tileFaceBorder: '#7a5a30',

  tileDivider: '#5c3a18',

  pipColor: '#1a0a04',
  pipShadow: 'rgba(40, 20, 8, 0.45)',

  tileBackBase: '#4a230f',
  tileBackPattern: '#2c1308',
  tileBackBorder: '#1a0a04',

  selectedRing: '#f4d894',
  validArmGlow: 'rgba(120, 220, 140, 0.85)',
};

export const PERSIAN_THEME: Theme = {
  ...BASE_PALETTES.persian,
  monogram: 'D',

  tileFaceBase: '#ece2c0',
  tileFaceHighlight: '#fff7d8',
  tileFaceShadow: '#bca888',
  tileFaceBorder: '#8a6028',

  tileDivider: '#1a2f44',

  pipColor: '#0a1820',
  pipShadow: 'rgba(8, 20, 28, 0.45)',

  tileBackBase: '#1a3848',
  tileBackPattern: '#0a1820',
  tileBackBorder: '#06141c',

  selectedRing: '#f0d28a',
  validArmGlow: 'rgba(58, 166, 176, 0.85)',
};

export const DECO_THEME: Theme = {
  ...BASE_PALETTES.deco,
  monogram: 'D',

  tileFaceBase: '#f2ecdc',
  tileFaceHighlight: '#fff8e8',
  tileFaceShadow: '#bcb088',
  tileFaceBorder: '#8a7036',

  tileDivider: '#0a0908',

  pipColor: '#0a0908',
  pipShadow: 'rgba(0, 0, 0, 0.45)',

  tileBackBase: '#1a1816',
  tileBackPattern: '#0a0908',
  tileBackBorder: '#040404',

  selectedRing: '#f0d898',
  validArmGlow: 'rgba(240, 216, 152, 0.9)',
};

export const MONTE_THEME: Theme = {
  ...BASE_PALETTES.monte,
  monogram: 'D',

  tileFaceBase: '#f2ecd8',
  tileFaceHighlight: '#fff8e0',
  tileFaceShadow: '#bcb088',
  tileFaceBorder: '#8a7036',

  tileDivider: '#0f2418',

  pipColor: '#0a0a0a',
  pipShadow: 'rgba(0, 0, 0, 0.45)',

  tileBackBase: '#2a160c',
  tileBackPattern: '#150a04',
  tileBackBorder: '#06150c',

  selectedRing: '#f0d898',
  validArmGlow: 'rgba(120, 220, 140, 0.85)',
};

export const THEMES: Record<ThemeName, Theme> = {
  mahogany: MAHOGANY_THEME,
  persian: PERSIAN_THEME,
  deco: DECO_THEME,
  monte: MONTE_THEME,
};
